// Level abstraction.
//
// A Level owns:
//   - All scene contents (meshes, lights, sky color)
//   - The player physics body
//   - The bungee cord (physics + visual)
//   - Per-life state (inJump)
//   - Spawn pose used by reset()
//
// A Level does NOT own:
//   - The Three.js Scene instance (passed into build())
//   - The camera (main.js positions it from level.player.position each frame)
//   - The renderer, input, or game state
//
// Lifecycle: build(scene) → many update(dt) calls → optionally reset() →
// eventually dispose() to free GPU buffers when swapping to a new level.

import * as THREE from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { Water } from 'three/examples/jsm/objects/Water.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PhysicalBody, BungeeCord } from './physics.js';

// ─── Procedural terrain helpers ───────────────────────────────────
// Inline deterministic value-noise + height function. Replace with a
// proper terrain pipeline if more levels need their own heightmaps.

function hash2(ix, iz) {
	let h = (ix | 0) * 374761393 + (iz | 0) * 668265263;
	h = (h ^ (h >>> 13)) * 1274126177;
	return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}
function smoothstep(t) { return t * t * (3 - 2 * t); }
function valueNoise2D(x, z) {
	const ix = Math.floor(x), iz = Math.floor(z);
	const fx = smoothstep(x - ix), fz = smoothstep(z - iz);
	const a = hash2(ix,     iz    );
	const b = hash2(ix + 1, iz    );
	const c = hash2(ix,     iz + 1);
	const d = hash2(ix + 1, iz + 1);
	return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
}
function fractalNoise(x, z) {
	let sum = 0, amp = 1, freq = 1, norm = 0;
	for (let i = 0; i < 4; i++) {
		sum += valueNoise2D(x * freq, z * freq) * amp;
		norm += amp;
		amp *= 0.5;
		freq *= 2;
	}
	return sum / norm; // [0, 1]
}

const ISLAND_RADIUS = 700;   // radius (xz) where bell mask reaches zero
const PEAK_HEIGHT   = 130;   // max noise amplitude at center
const BASE_HEIGHT   = 40;    // floor above sea level at center
const SEAFLOOR      = -45;   // ocean-floor baseline outside island
const NOISE_SCALE   = 0.004; // larger = more detail

function islandHeight(x, z) {
	const d = Math.sqrt(x * x + z * z);
	if (d >= ISLAND_RADIUS) {
		// Outer seafloor with mild dunes — masked by water in Phase 3.4.
		return SEAFLOOR + 15 * fractalNoise(x * 0.002, z * 0.002) - 15;
	}
	const t = d / ISLAND_RADIUS;
	// cos quarter-wave: 1 at center → 0 at edge, squared for steeper drop.
	const mask = Math.cos(t * Math.PI * 0.5);
	const m = mask * mask;
	const n = fractalNoise(x * NOISE_SCALE, z * NOISE_SCALE);
	return m * (BASE_HEIGHT + PEAK_HEIGHT * n);
}

// Linear-blend colors keyed by terrain height. Reads as wet sand → beach
// → grass → cliff → light rock as you climb.
const COLOR_BANDS = [
	{ h: -40, c: new THREE.Color(0x3a2f24) }, // deep mud
	{ h:  -2, c: new THREE.Color(0x6e5e44) }, // wet sand
	{ h:   4, c: new THREE.Color(0xc8b780) }, // beach
	{ h:  12, c: new THREE.Color(0x6e7a3c) }, // sparse grass
	{ h:  35, c: new THREE.Color(0x4a6028) }, // grass
	{ h:  80, c: new THREE.Color(0x394d1f) }, // dark grass
	{ h: 100, c: new THREE.Color(0x60554a) }, // soil/rock transition
	{ h: 140, c: new THREE.Color(0x8a8278) }, // light rock
];
function colorForHeight(h, target, bands = COLOR_BANDS) {
	if (h <= bands[0].h) return target.copy(bands[0].c);
	for (let i = 1; i < bands.length; i++) {
		if (h <= bands[i].h) {
			const t = (h - bands[i - 1].h) / (bands[i].h - bands[i - 1].h);
			return target.copy(bands[i - 1].c).lerp(bands[i].c, t);
		}
	}
	return target.copy(bands[bands.length - 1].c);
}

// Build a height-mapped, vertex-colored terrain mesh. Shared by every level
// that wants procedural ground: pass a height function (x,z)→y and a color
// function (y,target,x,z)→fills target. Returns a shadow-casting Mesh.
function buildTerrainMesh({ size = 2400, segs = 240, heightFn, colorFn, material }) {
	const geom = new THREE.PlaneGeometry(size, size, segs, segs);
	geom.rotateX(-Math.PI / 2); // verts now live in the world XZ plane

	const positions = geom.attributes.position;
	const colorArr = new Float32Array(positions.count * 3);
	const tmp = new THREE.Color();
	for (let i = 0; i < positions.count; i++) {
		const x = positions.getX(i);
		const z = positions.getZ(i);
		const y = heightFn(x, z);
		positions.setY(i, y);
		colorFn(y, tmp, x, z);
		colorArr[i * 3]     = tmp.r;
		colorArr[i * 3 + 1] = tmp.g;
		colorArr[i * 3 + 2] = tmp.b;
	}
	geom.setAttribute('color', new THREE.BufferAttribute(colorArr, 3));
	geom.computeVertexNormals();

	const mesh = new THREE.Mesh(
		geom,
		material ?? new THREE.MeshStandardMaterial({
			vertexColors: true, roughness: 0.95, metalness: 0.0,
		}),
	);
	mesh.receiveShadow = true;
	mesh.castShadow = true;
	return mesh;
}

// ─── Procedural tree geometry ─────────────────────────────────────
// Each species merges a few primitives into one BufferGeometry so the
// whole tree renders as a single InstancedMesh. Vertex colors carry
// trunk-vs-canopy differentiation (one material, no submeshes).

function paintGeometry(geom, hex) {
	const color = new THREE.Color(hex);
	const n = geom.attributes.position.count;
	const arr = new Float32Array(n * 3);
	for (let i = 0; i < n; i++) {
		arr[i * 3]     = color.r;
		arr[i * 3 + 1] = color.g;
		arr[i * 3 + 2] = color.b;
	}
	geom.setAttribute('color', new THREE.BufferAttribute(arr, 3));
	return geom;
}

function makePineGeometry() {
	const parts = [];
	const trunk = new THREE.CylinderGeometry(0.5, 0.9, 5, 6);
	trunk.translate(0, 2.5, 0);
	parts.push(paintGeometry(trunk, 0x5a4030));

	// Three stacked cones — classic stylized pine silhouette.
	const cones = [
		{ r: 2.8, h: 4.0, y: 5.0 },
		{ r: 2.2, h: 3.5, y: 7.0 },
		{ r: 1.5, h: 3.0, y: 9.0 },
	];
	for (const cs of cones) {
		const c = new THREE.ConeGeometry(cs.r, cs.h, 8);
		c.translate(0, cs.y + cs.h / 2, 0);
		parts.push(paintGeometry(c, 0x2d4a1f));
	}
	return mergeGeometries(parts, false);
}

function makePalmGeometry() {
	const parts = [];
	// Trunk: tall, narrow, slight taper.
	const trunk = new THREE.CylinderGeometry(0.4, 0.7, 11, 6);
	trunk.translate(0, 5.5, 0);
	parts.push(paintGeometry(trunk, 0x7a5530));

	// Crown: 7 fronds radiating, each a stretched cone tilted outward
	// from the trunk top.
	const N_FRONDS = 7;
	for (let i = 0; i < N_FRONDS; i++) {
		const frond = new THREE.ConeGeometry(0.5, 4.5, 4);
		frond.translate(0, 2.2, 0);
		frond.rotateZ(Math.PI / 2 + 0.45); // tilt outward & droop
		const angle = (i / N_FRONDS) * Math.PI * 2;
		frond.rotateY(angle);
		frond.translate(0, 11, 0);
		parts.push(paintGeometry(frond, 0x2e6a33));
	}

	// Coconut cluster — small dark sphere just under the crown.
	const cocos = new THREE.SphereGeometry(0.5, 6, 4);
	cocos.translate(0.4, 10.6, 0.3);
	parts.push(paintGeometry(cocos, 0x4a2f1a));

	return mergeGeometries(parts, false);
}

// Slope-aware scatter: returns a list of Matrix4 placements (position +
// yaw rotation + uniform random scale). Position distribution is
// uniform-by-area inside the island disk — without sqrt() on the
// radius, point density is heavily biased toward the center because
// sampling (uniform angle, uniform radius) over-weights the inner ring
// per unit area.
function makeScatterMatrices(opts) {
	const { count, accept, scaleMin, scaleMax, sinkInto = 0.5, heightFn = islandHeight } = opts;
	const out = [];
	const pos = new THREE.Vector3();
	const quat = new THREE.Quaternion();
	const scale = new THREE.Vector3();
	const Y = new THREE.Vector3(0, 1, 0);
	const MAX_TRIES = count * 20;
	for (let tries = 0; out.length < count && tries < MAX_TRIES; tries++) {
		const angle = Math.random() * Math.PI * 2;
		const r = Math.sqrt(Math.random()) * (ISLAND_RADIUS - 20); // uniform-by-area
		const x = Math.cos(angle) * r;
		const z = Math.sin(angle) * r;
		const y = heightFn(x, z);
		if (!accept(y)) continue;
		pos.set(x, y - sinkInto, z);
		quat.setFromAxisAngle(Y, Math.random() * Math.PI * 2);
		const s = scaleMin + Math.random() * (scaleMax - scaleMin);
		scale.set(s, s, s);
		const m = new THREE.Matrix4();
		m.compose(pos, quat, scale);
		out.push(m);
	}
	return out;
}

function instancedFromMatrices(geom, material, matrices, premultiply) {
	const mesh = new THREE.InstancedMesh(geom, material, matrices.length);
	mesh.castShadow = true;
	mesh.receiveShadow = true;
	const tmp = new THREE.Matrix4();
	for (let i = 0; i < matrices.length; i++) {
		if (premultiply) {
			tmp.multiplyMatrices(matrices[i], premultiply);
			mesh.setMatrixAt(i, tmp);
		} else {
			mesh.setMatrixAt(i, matrices[i]);
		}
	}
	mesh.instanceMatrix.needsUpdate = true;
	return mesh;
}

// Walk a GLTF scene, gather each Mesh's geometry + material along with
// the mesh's local transform (relative to a normalized base where the
// trunk bottom sits at y=0 and total height ≈ targetHeight). Returns
// a list of { geometry, material, localMatrix } usable per-instance.
function gltfTreeParts(gltfScene, targetHeight) {
	gltfScene.updateMatrixWorld(true);
	const box = new THREE.Box3().setFromObject(gltfScene);
	const h = box.max.y - box.min.y || 1;
	const norm = targetHeight / h;
	// Build a "world → normalized base" transform: translate so bottom
	// is at origin, then uniformly scale to target height.
	const normMat = new THREE.Matrix4()
		.makeTranslation(0, -box.min.y, 0)
		.premultiply(new THREE.Matrix4().makeScale(norm, norm, norm));

	const parts = [];
	gltfScene.traverse((o) => {
		if (!o.isMesh) return;
		const localMatrix = new THREE.Matrix4()
			.copy(normMat)
			.multiply(o.matrixWorld);
		parts.push({
			geometry: o.geometry,
			material: o.material,
			localMatrix,
		});
	});
	return parts;
}

// Water normal map, loaded once and shared across every level (re)build.
// Building a fresh TextureLoader().load() per build re-fetched the file and
// leaked a GPU texture each time (Material.dispose() doesn't free uniform
// textures). Loading it once — and intentionally keeping it for the whole
// session — sidesteps both. Safe to share: textures are immutable here.
// Shared zero vector for the cut-cord free fall (no spring/tangential force).
const _ZERO = new THREE.Vector3();

let _waterNormals = null;
function sharedWaterNormals() {
	if (!_waterNormals) {
		_waterNormals = new THREE.TextureLoader().load(
			'textures/waternormals.jpg',
			(tex) => { tex.wrapS = tex.wrapT = THREE.RepeatWrapping; },
		);
	}
	return _waterNormals;
}

export class Level {
	constructor() {
		this.scene = null;
		this._tracked = []; // meshes/lights to remove + dispose on teardown

		this.player = null;
		this.cord = null;
		this.cordLine = null;
		this._cordPositions = null;
		this._spawnPosition = new THREE.Vector3();
		this._spawnVelocity = new THREE.Vector3();
		this.inJump = false;
		this.water = null; // optional — set by subclasses that add a Water mesh

		// Semantic gameplay events for audio + the hell easter egg. Same
		// on/_emit shape as Game / InputManager / AssetLoader.
		//   jump  (speed) — slack→falling transition
		//   catch (speed) — cord crosses from slack to taut
		//   death ()      — cut-cord free-fall passed deathY (→ hell respawn)
		this._listeners = { jump: [], catch: [], death: [] };
		// Edge-detect state so the event fires once per transition.
		this._wasTaut = false;

		// Hell easter egg: cutting the cord mid-jump (press jump again) drops
		// the player into free fall; once they pass deathY, `death` fires.
		this._cordCut = false;
		this._dead = false;
		this.deathY = -120; // below every level's terrain; subclasses may tweak

		// Tree InstancedMeshes tracked per species so setTreeDensity() can
		// vary their `count` at runtime. Each "tree" can be made of multiple
		// meshes (trunk + leaves) — all share the same scatter matrices in
		// the same order, so setting the same .count on all of them keeps
		// the tree pieces in sync.
		this._treeMeshes = { pine: [], palm: [] };
		this._treeDensity = 1.0;
	}

	on(event, callback) {
		if (!this._listeners[event]) {
			throw new Error(`Level: unknown event "${event}"`);
		}
		this._listeners[event].push(callback);
		return this;
	}

	_emit(event, ...args) {
		for (const cb of this._listeners[event]) cb(...args);
	}

	setTreeDensity(d) {
		this._treeDensity = d;
		const pineN = Math.floor((this._pineMatrices?.length ?? 0) * d);
		const palmN = Math.floor((this._palmMatrices?.length ?? 0) * d);
		for (const m of this._treeMeshes.pine) m.count = pineN;
		for (const m of this._treeMeshes.palm) m.count = palmN;
	}

	// Optional hook: subclasses that stored per-tree scatter matrices in
	// build() register GLTF variants here, after assets finish loading.
	// No-op default.
	addGLTFTrees(/* assets */) {}

	// Optional hook: apply per-level renderer settings (tone-mapping exposure,
	// etc.) on swap. Called by main.js after build(). Each level that overrides
	// it should set everything it cares about, since the previous level may
	// have changed the shared renderer. No-op default.
	configureRenderer(/* renderer */) {}

	// Override in subclasses to populate `this.scene`, set spawn pose, etc.
	build(/* scene */) { throw new Error('Level: build() must be overridden'); }

	// Tear down: remove tracked objects from the scene and free their GPU
	// resources. Safe to call multiple times. Called on every level swap, so
	// it has to be leak-free *and* must not clobber shared resources.
	dispose() {
		if (!this.scene) return;
		for (const obj of this._tracked) {
			this.scene.remove(obj);

			// InstancedMesh owns per-instance buffers (the instanceMatrix);
			// dispose() frees those. The geometry/material may still be shared
			// (see sharedAsset below), so this is separate from disposing them.
			if (obj.isInstancedMesh && typeof obj.dispose === 'function') obj.dispose();

			// Directional/spot lights own a shadow-map render target that the
			// renderer never frees on its own — drop it or it leaks per swap.
			if (obj.shadow && obj.shadow.map) {
				obj.shadow.map.dispose();
				obj.shadow.map = null;
			}

			// sharedAsset objects (GLTF tree instances) borrow their geometry
			// and material from the loaded asset cache — they're reused by the
			// next level build, so we must NOT dispose them here.
			if (obj.userData && obj.userData.sharedAsset) continue;

			if (obj.geometry) obj.geometry.dispose();
			if (obj.material) {
				const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
				for (const m of mats) m.dispose();
			}
		}
		this._tracked.length = 0;
		// Reset shared scene state a level may have set, so it doesn't bleed
		// into the next level (e.g. the moon sets a dark background).
		this.scene.background = null;
		this.scene.fog = null;
		this.scene = null;
	}

	// Per-frame update. Visual ticks (water animation) run whenever this
	// is called; main.js gates the call on game state. Physics is further
	// gated on `inJump` so a not-yet-jumping player stays still on splash.
	update(dt) {
		if (this.water) {
			this.water.material.uniforms.time.value += dt;
		}

		if (!this.inJump) return;

		// Cord cut → free fall: gravity + drag only, no spring/tangential, and
		// the cord visual is hidden. Once we plunge past the kill plane, `death`
		// fires once (→ hell respawn, wired in main.js).
		if (this._cordCut) {
			this.player.updateForce(_ZERO, _ZERO);
			this.player.force2acc();
			this.player.acc2vel(dt);
			this.player.vel2pos(dt);
			if (!this._dead && this.player.position.y < this.deathY) {
				this._dead = true;
				this._emit('death');
			}
			return;
		}

		this.cord.updateSpringForce(this.player.velocity);
		this.cord.updateTangentialForce(this.player.gravityForce);

		// Catch: cord crossing slack→taut. ropeDisplacement was just refreshed
		// by the force updates above. Fire with the impact speed so a consumer
		// can scale the effect; subsequent bounces re-taut and fire again,
		// softer. (Currently unconsumed — the synthesized twang was dropped.)
		const taut = this.cord.ropeDisplacement.length() > this.cord.ropeLength;
		if (taut && !this._wasTaut) this._emit('catch', this.player.velocity.length());
		this._wasTaut = taut;

		this.player.updateForce(this.cord.springForce, this.cord.tangentialForce);
		this.player.force2acc();
		this.player.acc2vel(dt);
		this.player.vel2pos(dt);

		// Cord visual: player-end vertex follows player position.
		this._cordPositions[3] = this.player.position.x;
		this._cordPositions[4] = this.player.position.y;
		this._cordPositions[5] = this.player.position.z;
		this.cordLine.geometry.attributes.position.needsUpdate = true;
	}

	// Cut the cord mid-jump (the hell easter egg). Drops the player into free
	// fall and hides the cord line. No-op unless currently falling on a cord.
	cutCord() {
		if (!this.inJump || this._cordCut) return;
		this._cordCut = true;
		if (this.cordLine) this.cordLine.visible = false;
	}

	// Begin the bungee jump (player starts falling). No-op if already falling
	// — guarding on inJump means the `jump` event fires once per life, not on
	// every repeated keypress.
	//
	// `heading` (optional) is a horizontal unit vector — the camera's facing
	// direction flattened onto the ground plane (main.js owns the yaw→world
	// mapping and passes it in). The launch keeps the per-level spawn *speed*
	// but redirects it along the look direction, so you leap off the platform
	// the way you're facing instead of always firing down the same fixed axis.
	triggerJump(heading) {
		if (this.inJump) return;
		this.inJump = true;
		// The player sat at rest on the platform; apply the launch speed now,
		// along the look heading. No heading (programmatic call) → fire forward.
		if (heading) {
			this.player.velocity.copy(heading).setY(0).normalize().multiplyScalar(this._launchSpeed);
		} else {
			this.player.velocity.set(0, 0, -this._launchSpeed);
		}
		this._emit('jump', this.player.velocity.length());
	}

	// Restart the current life: player back to spawn, cord slack, no fall in progress.
	// Doesn't touch the scene contents — just resets dynamic state.
	reset() {
		this.inJump = false;
		this._wasTaut = false;
		this._cordCut = false;
		this._dead = false;
		if (this.cordLine) this.cordLine.visible = true;
		this.player.position.copy(this._spawnPosition);
		this.player.velocity.copy(this._spawnVelocity);
		this.player.acceleration.set(0, 0, 0);
		this.player.force.set(0, 0, 0);

		this._cordPositions[3] = this.player.position.x;
		this._cordPositions[4] = this.player.position.y;
		this._cordPositions[5] = this.player.position.z;
		this.cordLine.geometry.attributes.position.needsUpdate = true;
	}

	// Helpers for subclasses: add a mesh/light and track it for disposal.
	_add(obj) {
		this.scene.add(obj);
		this._tracked.push(obj);
		return obj;
	}

	// Helper for subclasses: build a bungee crane (vertical mast + horizontal
	// arm + anchor ball) standing on the terrain. The base sits at the given
	// terrain height so the tower neither floats nor buries. Returns the
	// world-space anchor position to hand to _initBungee.
	_addBungeeTower({ baseY, height = 400, offset = 18, towerColor = 0x555555, anchorColor = 0xffcc33 }) {
		const towerMat = new THREE.MeshStandardMaterial({ color: towerColor, roughness: 0.5, metalness: 0.4 });

		const mast = this._add(new THREE.Mesh(
			new THREE.CylinderGeometry(2.5, 4, height, 8), towerMat,
		));
		mast.position.set(-offset, baseY + height / 2, 0);
		mast.castShadow = true;
		mast.receiveShadow = true;

		const arm = this._add(new THREE.Mesh(
			new THREE.BoxGeometry(offset, 3, 3), towerMat,
		));
		arm.position.set(-offset / 2, baseY + height + 3, 0);
		arm.castShadow = true;
		arm.receiveShadow = true;

		const anchorPos = new THREE.Vector3(0, baseY + height, 0);
		const anchor = this._add(new THREE.Mesh(
			new THREE.SphereGeometry(4, 16, 12),
			new THREE.MeshStandardMaterial({ color: anchorColor, roughness: 0.5 }),
		));
		anchor.position.copy(anchorPos);
		anchor.castShadow = true;

		return anchorPos;
	}

	// Helper for subclasses: wire up player + cord + cord visual given a
	// spawn position, anchor position, rope length, the horizontal launch
	// speed (applied along the look heading in triggerJump), and an optional
	// gravity override (the moon dials this down for a floaty jump). The player
	// spawns at rest, so the HUD reads 0 speed until they actually leap.
	_initBungee({ spawnPosition, anchorPosition, ropeLength, gravityAcc, launchSpeed = 50 }) {
		this._spawnPosition.copy(spawnPosition);
		this._spawnVelocity.set(0, 0, 0); // at rest on the platform
		this._launchSpeed = launchSpeed;

		this.player = new PhysicalBody({
			position: spawnPosition,
			velocity: this._spawnVelocity,
			gravityAcc,
		});

		this.cord = new BungeeCord({
			ropeLength,
			ropeOrigin: anchorPosition,
			ropePosition: this.player.position,
		});

		// Two-vertex line; we mutate the player-end each frame.
		this._cordPositions = new Float32Array([
			anchorPosition.x, anchorPosition.y, anchorPosition.z,
			this.player.position.x, this.player.position.y, this.player.position.z,
		]);
		const cordGeom = new THREE.BufferGeometry();
		cordGeom.setAttribute('position', new THREE.BufferAttribute(this._cordPositions, 3));
		this.cordLine = new THREE.Line(
			cordGeom,
			new THREE.LineBasicMaterial({ color: 0x222222 }),
		);
		this._add(this.cordLine);
	}
}

// PlaygroundLevel: the current Phase 2 placeholder world. Grid, tower,
// pillars, flat ground. Phase 3 replaces this with a real island level.
export class PlaygroundLevel extends Level {
	build(scene) {
		this.scene = scene;

		// Sky shader (Rayleigh + Mie scattering). Renders as a huge inverted
		// box; scale must fit inside camera.far (7000). The sun direction is
		// stored on `this.sunPosition` so Phase 3.2 can hang a DirectionalLight
		// off the same vector for matching shadow direction.
		const sky = this._add(new Sky());
		sky.scale.setScalar(4500);
		const skyU = sky.material.uniforms;
		// Lower turbidity + mieCoefficient → less haze, dimmer visible sun
		// disc. mieDirectionalG also lowered slightly so the forward
		// scatter cone is broader → softer halo, not a laser-point sun.
		skyU.turbidity.value       = 4;
		skyU.rayleigh.value        = 2;
		skyU.mieCoefficient.value  = 0.0015;
		skyU.mieDirectionalG.value = 0.7;

		const elevationDeg = 55;  // higher overhead — more even top-down lighting, less "horizon haze" framing
		const azimuthDeg   = 135; // 0 = north (+Z), 90 = east (+X)
		const phi   = THREE.MathUtils.degToRad(90 - elevationDeg);
		const theta = THREE.MathUtils.degToRad(azimuthDeg);
		this.sunPosition = new THREE.Vector3().setFromSphericalCoords(1, phi, theta);
		skyU.sunPosition.value.copy(this.sunPosition);

		// ─── Water ───────────────────────────────────────────────
		// three/examples Water: planar reflection + animated normals.
		// Sun direction matches the Sky so the reflected glint lands where
		// the visible sun is. Texture is vendored under /public/textures/ and
		// shared across builds (see sharedWaterNormals).
		//
		// NOTE: Water also allocates an internal reflection render target it
		// doesn't expose, so each Water we build-then-dispose leaks that one
		// target. The island is built infrequently (startup, hell-respawn
		// return) and the moon/hell levels don't use Water, so this is
		// acceptable; revisit if a level cycles Water in and out repeatedly.
		const water = new Water(
			new THREE.PlaneGeometry(8000, 8000),
			{
				textureWidth: 512,
				textureHeight: 512,
				waterNormals: sharedWaterNormals(),
				sunDirection: this.sunPosition.clone().normalize(),
				// Sun reflection deliberately near-black (0.04 grey) — the
				// Sky shader's HDR sun already lights the water plenty via
				// the planar reflection; adding bright specular on top blew
				// out the whole sun side of the scene. Tuned via tweak panel.
				sunColor: new THREE.Color(0.04, 0.04, 0.04),
				// HSL(191°, 74%, 13%) — tropical teal hue, dim lightness so
				// the island reads brighter by comparison. Also tweak-tuned.
				waterColor: new THREE.Color().setHSL(0.531, 0.74, 0.13),
				distortionScale: 3.7,
				fog: false,
			},
		);
		water.rotation.x = -Math.PI / 2;
		water.position.y = 0; // sea level — island bell mask reaches 0 at radius 700
		this.water = this._add(water);

		// ─── Terrain ─────────────────────────────────────────────
		// Procedural island: radial bell × fractal noise, vertex colors
		// banded by height (COLOR_BANDS). PBR textures deferred — the lit
		// vertex-color look reads well enough on its own.
		this._add(buildTerrainMesh({
			heightFn: islandHeight,
			colorFn: (y, target) => colorForHeight(y, target),
		}));

		// Bungee crane, base at the sampled terrain height so it neither
		// floats nor buries. Default grey mast + yellow anchor.
		const TOWER_OFFSET = 18;
		const anchorPos = this._addBungeeTower({
			baseY: islandHeight(-TOWER_OFFSET, 0),
			offset: TOWER_OFFSET,
		});

		// ─── Trees: scatter once, instance twice ─────────────────
		// Positions are computed up front (max count) and stored on the
		// level for the GLTF pass (addGLTFTrees) to reuse. The runtime
		// density slider varies each InstancedMesh's `.count` to render
		// a prefix of the matrices — since scatter order is random,
		// the prefix is itself a uniform random subset.
		this._pineMatrices = makeScatterMatrices({
			count: 1500, accept: (y) => y > 14 && y < 110,
			scaleMin: 2.2, scaleMax: 4.4,
		});
		this._palmMatrices = makeScatterMatrices({
			count: 420, accept: (y) => y > 0.5 && y < 14,
			scaleMin: 2.0, scaleMax: 3.6,
		});

		// Procedural trees lost the A/B and are disabled by default. The
		// geometry helpers (makePineGeometry / makePalmGeometry /
		// paintGeometry) and scatter machinery stay defined as a
		// code-level fallback. Flip this flag to true to add them back
		// to the scene — note they'll then render simultaneously with the
		// GLTF trees unless you also stop the GLTF loads in main.js.
		const ENABLE_PROCEDURAL_TREES = false;
		if (ENABLE_PROCEDURAL_TREES) {
			const foliageMat = new THREE.MeshStandardMaterial({
				vertexColors: true,
				roughness: 0.85,
				metalness: 0.0,
			});
			const ppine = instancedFromMatrices(
				makePineGeometry(), foliageMat, this._pineMatrices,
			);
			const ppalm = instancedFromMatrices(
				makePalmGeometry(), foliageMat, this._palmMatrices,
			);
			this._add(ppine); this._treeMeshes.pine.push(ppine);
			this._add(ppalm); this._treeMeshes.palm.push(ppalm);
		}

		// Lighting. Direction matches the Sky shader's sun so cast shadows and
		// shaded sides line up with the visible sun glow. HemisphereLight is
		// the *fill light* — it's what brightens everything not in direct
		// sun. Ratio of direct to fill is now ~1.8:1, which reads as "bright
		// outdoor day" rather than the previous 5:1 which read as "overcast."
		const SUN_DISTANCE = 1500;
		const sun = this._add(new THREE.DirectionalLight(0xfff4e0, 2.0));
		sun.position.copy(this.sunPosition).multiplyScalar(SUN_DISTANCE);
		sun.castShadow = true;
		// 1024² shadow map (was 2048²). PCFSoftShadowMap's PCF blur masks
		// the lower texel count well; halving the dimension is 4× less
		// shadow-pass work, which is the dominant GPU cost at ~2k tree
		// instances. Bump back to 2048 only if shadows look visibly mushy.
		sun.shadow.mapSize.set(1024, 1024);
		// Shadow camera frustum: covers the island (radius 700) and the
		// elevated tower top. Larger frustum → slightly blurrier shadows
		// per texel; 2048² map keeps it readable.
		const SH = 750;
		sun.shadow.camera.left = -SH;
		sun.shadow.camera.right = SH;
		sun.shadow.camera.top = SH;
		sun.shadow.camera.bottom = -SH;
		sun.shadow.camera.near = SUN_DISTANCE - 800;
		sun.shadow.camera.far  = SUN_DISTANCE + 800;
		sun.shadow.bias = -0.0005; // suppress shadow acne on near-tangent surfaces

		this._add(new THREE.HemisphereLight(0xb1d2ff, 0x4a4030, 2.8));

		// Player + cord. Spawn at the anchor so the jump starts taut-cord-down.
		this._initBungee({
			spawnPosition:   anchorPos.clone(),
			anchorPosition:  anchorPos,
			ropeLength:      200,
			launchSpeed:     50,
		});
	}

	// The island is lit by the HDR Sky shader; this exposure was calibrated
	// against the sun + hemisphere intensities in build().
	configureRenderer(renderer) {
		renderer.toneMappingExposure = 0.5;
	}

	// Wire up GLTF variants of pine + palm using the same scatter matrices
	// computed in build(). `assets.pineGLTF` / `assets.palmGLTF` may be
	// null (AssetLoader fails soft on missing files); whichever species
	// didn't load simply doesn't appear. Each GLTF mesh becomes its own
	// InstancedMesh so multi-mesh trees (trunk + leaves) compose cleanly.
	addGLTFTrees(assets) {
		const PINE_TARGET_H = 10; // unscaled height target for the high-elevation slot
		const PALM_TARGET_H = 11; // unscaled height target for the low-ring slot

		if (assets.pineGLTF && this._pineMatrices) {
			for (const part of gltfTreeParts(assets.pineGLTF.scene, PINE_TARGET_H)) {
				const m = instancedFromMatrices(
					part.geometry, part.material, this._pineMatrices, part.localMatrix,
				);
				// geometry + material belong to the loaded GLTF — don't let
				// dispose() free them, the next level build reuses them.
				m.userData.sharedAsset = true;
				this._add(m);
				this._treeMeshes.pine.push(m);
			}
		}
		if (assets.palmGLTF && this._palmMatrices) {
			for (const part of gltfTreeParts(assets.palmGLTF.scene, PALM_TARGET_H)) {
				const m = instancedFromMatrices(
					part.geometry, part.material, this._palmMatrices, part.localMatrix,
				);
				m.userData.sharedAsset = true;
				this._add(m);
				this._treeMeshes.palm.push(m);
			}
		}
		// Apply current density to newly-added meshes (initially 1.0).
		this.setTreeDensity(this._treeDensity);
	}
}

// ─── Moon level helpers ───────────────────────────────────────────

// Grey regolith palette, dark crater floor → bright sunlit ridge.
const MOON_COLOR_BANDS = [
	{ h: -40, c: new THREE.Color(0x2e2e33) }, // crater floor (shadow)
	{ h:   0, c: new THREE.Color(0x55555c) }, // shadowed regolith
	{ h:  25, c: new THREE.Color(0x7c7c84) }, // mid grey
	{ h:  50, c: new THREE.Color(0x9a9aa2) }, // lit regolith
	{ h:  80, c: new THREE.Color(0xc2c2c8) }, // bright ridges
];

// A handful of craters, generated deterministically (via hash2) so the
// surface is stable across rebuilds.
const MOON_CRATERS = [];
for (let i = 0; i < 7; i++) {
	const a = hash2(i * 13 + 1, i * 7 + 3) * Math.PI * 2;
	const r = 120 + hash2(i * 5 + 2, i * 11 + 9) * 680;
	MOON_CRATERS.push({
		x: Math.cos(a) * r,
		z: Math.sin(a) * r,
		radius: 70 + hash2(i * 17, i * 3) * 160,
		depth: 24 + hash2(i * 2, i * 19) * 26,
	});
}

function moonHeight(x, z) {
	// Gentle rolling regolith — lower amplitude than the island, and a
	// different noise seed so it doesn't mirror it.
	let h = 38 * fractalNoise(x * 0.0016 + 50, z * 0.0016 + 50);
	// Craters: bowl with a raised rim. profile(n²) = (2n²−1)·e^(−n²),
	// n = d/radius → −1 at centre (deep), ~+0.37 near the rim, →0 outside.
	for (const c of MOON_CRATERS) {
		const dx = x - c.x, dz = z - c.z;
		const n2 = (dx * dx + dz * dz) / (c.radius * c.radius);
		if (n2 < 6) h += c.depth * (2 * n2 - 1) * Math.exp(-n2);
	}
	// Mild radial falloff so the square plane edge sinks out of view rather
	// than ending in a hard cliff against the starfield.
	const d = Math.sqrt(x * x + z * z);
	if (d > 1000) h -= (d - 1000) * 0.25;
	return h;
}

// A sphere of white point-stars. sizeAttenuation off → constant pixel size
// regardless of distance, so they read as crisp stars rather than blobs.
function makeStarfield(count = 1800, radius = 4000) {
	const positions = new Float32Array(count * 3);
	for (let i = 0; i < count; i++) {
		const u = Math.random() * 2 - 1;            // uniform on the sphere
		const theta = Math.random() * Math.PI * 2;
		const r = Math.sqrt(1 - u * u);
		positions[i * 3]     = Math.cos(theta) * r * radius;
		positions[i * 3 + 1] = u * radius;
		positions[i * 3 + 2] = Math.sin(theta) * r * radius;
	}
	const geom = new THREE.BufferGeometry();
	geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
	const stars = new THREE.Points(geom, new THREE.PointsMaterial({
		color: 0xffffff, size: 2, sizeAttenuation: false, depthWrite: false,
	}));
	stars.frustumCulled = false; // huge bounding sphere; just always draw it
	return stars;
}

// MoonLevel: low gravity, grey cratered regolith, a starfield instead of the
// Sky shader, scattered boulders, no water. Demonstrates the level-swap
// infrastructure with a genuinely different world.
export class MoonLevel extends Level {
	build(scene) {
		this.scene = scene;

		// Space: near-black background (no Sky shader). The starfield and this
		// colour are the entire sky.
		scene.background = new THREE.Color(0x04050a);
		this._add(makeStarfield());

		// Sun direction — low and raking for long dramatic shadows. No visible
		// disc; it only drives the directional light.
		const phi = THREE.MathUtils.degToRad(90 - 32);
		const theta = THREE.MathUtils.degToRad(120);
		this.sunPosition = new THREE.Vector3().setFromSphericalCoords(1, phi, theta);

		// Grey regolith terrain (matte — no specular on the moon).
		this._add(buildTerrainMesh({
			heightFn: moonHeight,
			colorFn: (y, target) => colorForHeight(y, target, MOON_COLOR_BANDS),
			material: new THREE.MeshStandardMaterial({
				vertexColors: true, roughness: 1.0, metalness: 0.0,
			}),
		}));

		// Crane: pale metal mast, red anchor (pops against the grey + black).
		const TOWER_OFFSET = 18;
		const anchorPos = this._addBungeeTower({
			baseY: moonHeight(-TOWER_OFFSET, 0),
			offset: TOWER_OFFSET,
			towerColor: 0xbfc4cc,
			anchorColor: 0xff5533,
		});

		// Scattered faceted boulders (own their geometry/material — not shared,
		// so dispose() frees them normally).
		const rockMatrices = makeScatterMatrices({
			count: 140, accept: (y) => y > -10 && y < 90,
			scaleMin: 1.5, scaleMax: 6.0, sinkInto: 0.8, heightFn: moonHeight,
		});
		this._add(instancedFromMatrices(
			new THREE.IcosahedronGeometry(2, 0),
			new THREE.MeshStandardMaterial({
				color: 0x6c6c72, roughness: 1.0, metalness: 0.0, flatShading: true,
			}),
			rockMatrices,
		));

		// Harsh sunlight + a faint cool fill. Space has almost no fill light,
		// but pure-black shadows read as broken, so a little keeps shapes legible.
		const SUN_DISTANCE = 1500;
		const sun = this._add(new THREE.DirectionalLight(0xffffff, 3.0));
		sun.position.copy(this.sunPosition).multiplyScalar(SUN_DISTANCE);
		sun.castShadow = true;
		sun.shadow.mapSize.set(1024, 1024);
		const SH = 750;
		sun.shadow.camera.left = -SH;
		sun.shadow.camera.right = SH;
		sun.shadow.camera.top = SH;
		sun.shadow.camera.bottom = -SH;
		sun.shadow.camera.near = SUN_DISTANCE - 800;
		sun.shadow.camera.far  = SUN_DISTANCE + 800;
		sun.shadow.bias = -0.0005;

		this._add(new THREE.HemisphereLight(0x404560, 0x05050a, 0.35));

		// Low-gravity bungee — ~1/4 g. Floaty fall, gentle bounce.
		this._initBungee({
			spawnPosition:  anchorPos.clone(),
			anchorPosition: anchorPos,
			ropeLength:     200,
			gravityAcc:     18,
			launchSpeed:    40,
		});
	}

	// No HDR sky here, so the island's 0.5 exposure would look murky — open
	// it up. The starfield + grey regolith are all LDR.
	configureRenderer(renderer) {
		renderer.toneMappingExposure = 1.1;
	}
}

// ─── Hell level helpers ───────────────────────────────────────────

// Scorched-rock palette: warm dark browns/greys (not near-black, so relief
// reads under the dim hell lighting) → dull lit ridge tops.
const HELL_COLOR_BANDS = [
	{ h: -45, c: new THREE.Color(0x2a1110) }, // scorched, lava-adjacent
	{ h:   5, c: new THREE.Color(0x3e2a25) }, // dark rock
	{ h:  45, c: new THREE.Color(0x584038) }, // mid rock (warm grey-brown)
	{ h:  85, c: new THREE.Color(0x735649) }, // lit rock
	{ h: 130, c: new THREE.Color(0x8f6a58) }, // ridge tops
];

function hellHeight(x, z) {
	// Rolling jagged rock that mostly sits ABOVE the lava plane (y=0); the
	// low ~quarter dips below it so lava shows through in the valleys/cracks.
	// Rock dominates the view (the earlier all-spires version submerged most
	// of the surface and read as black islands on flat red).
	const n = fractalNoise(x * 0.006 + 200, z * 0.006 + 200);
	return -45 + 160 * n;
}

// HellLevel: dark-red void + exponential fog, a glowing lava sea with jagged
// black spires, an iron crane rising from the lava, heavy gravity. The
// destination of the cut-cord "death" easter egg.
export class HellLevel extends Level {
	build(scene) {
		this.scene = scene;

		// Oppressive dark-red void + matching exponential fog so the spires and
		// lava sea recede into haze. Background ≈ fog colour → seamless horizon
		// (and it hides every plane edge).
		scene.background = new THREE.Color(0x1a0604);
		scene.fog = new THREE.FogExp2(0x1a0604, 0.00055);

		// Lava: a big emissive plane at y=0. The terrain mostly rises above it;
		// lava fills the low valleys/cracks. emissiveIntensity pulses in update().
		const lava = new THREE.Mesh(
			new THREE.PlaneGeometry(8000, 8000),
			new THREE.MeshStandardMaterial({
				color: 0x200200, emissive: 0xff4a00, emissiveIntensity: 0.95,
				roughness: 0.5, metalness: 0.0,
			}),
		);
		lava.rotation.x = -Math.PI / 2;
		lava.position.y = 0;
		this.lava = this._add(lava);

		// Charred jagged terrain.
		this._add(buildTerrainMesh({
			heightFn: hellHeight,
			colorFn: (y, target) => colorForHeight(y, target, HELL_COLOR_BANDS),
			material: new THREE.MeshStandardMaterial({
				vertexColors: true, roughness: 1.0, metalness: 0.0,
			}),
		}));

		// Dark iron crane rising out of the lava, glowing-red anchor.
		const TOWER_OFFSET = 18;
		const anchorPos = this._addBungeeTower({
			baseY: hellHeight(-TOWER_OFFSET, 0),
			height: 460, offset: TOWER_OFFSET,
			towerColor: 0x141414, anchorColor: 0xff2a00,
		});

		// Scattered dark spires for texture, on the rock above the lava line.
		const spireMatrices = makeScatterMatrices({
			count: 80, accept: (y) => y > 10 && y < 120,
			scaleMin: 1.2, scaleMax: 3.2, sinkInto: 1.0, heightFn: hellHeight,
		});
		this._add(instancedFromMatrices(
			new THREE.ConeGeometry(3, 16, 5),
			new THREE.MeshStandardMaterial({
				color: 0x2a1813, roughness: 1.0, metalness: 0.0, flatShading: true,
			}),
			spireMatrices,
		));

		// Warm orange "sun" from fairly high up so it actually lights the rock
		// tops (the camera looks down at them), plus a hemisphere whose hot
		// orange *ground* colour fakes the lava's uplight bounce from below and
		// whose warm *sky* colour keeps the tops from going black.
		const SUN_DISTANCE = 1500;
		const phi = THREE.MathUtils.degToRad(90 - 50);
		const theta = THREE.MathUtils.degToRad(60);
		this.sunPosition = new THREE.Vector3().setFromSphericalCoords(1, phi, theta);
		const sun = this._add(new THREE.DirectionalLight(0xff8a4a, 2.2));
		sun.position.copy(this.sunPosition).multiplyScalar(SUN_DISTANCE);
		sun.castShadow = true;
		sun.shadow.mapSize.set(1024, 1024);
		const SH = 750;
		sun.shadow.camera.left = -SH;
		sun.shadow.camera.right = SH;
		sun.shadow.camera.top = SH;
		sun.shadow.camera.bottom = -SH;
		sun.shadow.camera.near = SUN_DISTANCE - 800;
		sun.shadow.camera.far  = SUN_DISTANCE + 800;
		sun.shadow.bias = -0.0005;

		this._add(new THREE.HemisphereLight(0x4a2a1c, 0xff5a1e, 1.2));

		// Heavy gravity — dragged down to hell.
		this._initBungee({
			spawnPosition:  anchorPos.clone(),
			anchorPosition: anchorPos,
			ropeLength:     200,
			gravityAcc:     90,
			launchSpeed:    50,
		});
	}

	update(dt) {
		super.update(dt);
		if (this.lava) {
			this._lavaT = (this._lavaT || 0) + dt;
			this.lava.material.emissiveIntensity = 0.85 + 0.15 * Math.sin(this._lavaT * 1.4);
		}
	}

	// Emissive lava is bright; pull exposure down from the island's 0.5 a touch
	// so the lava glows without blowing out and the rock stays appropriately dark.
	configureRenderer(renderer) {
		renderer.toneMappingExposure = 0.65;
	}
}
